import { Aviso } from "@/components/Aviso"
import { Bloco } from "@/components/Carregando"
import { Marca } from "@/components/Marca"
import { Spinner } from "@/components/Spinner"
import { ApiError } from "@/lib/api"
import { useMutation, useQuery } from "@tanstack/react-query"
import { type FormEvent, useId, useState } from "react"
import { useSearchParams } from "react-router"
import { FOCO } from "../repasse/components/foco"
import { descadastrar, fetchDestinatario } from "./api"
import { type Descadastro, DescadastroSchema, LIMITE_DO_TEXTO, MOTIVOS } from "./schema"

const BOTAO = `inline-flex min-h-11 items-center gap-2 self-start rounded-full bg-brand px-6 py-2.5 font-display font-semibold text-brand-ink transition-colors hover:bg-brand-strong disabled:opacity-60 ${FOCO}`

const CAMPO = `w-full rounded-lg border border-line-strong bg-card px-3 py-2 text-ink ${FOCO}`

const linkRecusado = (erro: unknown) => erro instanceof ApiError && erro.falha === "link"

const mensagemDe = (erro: unknown) =>
    erro instanceof ApiError && erro.mensagem
        ? erro.mensagem
        : erro instanceof ApiError && erro.falha === "rede"
          ? "Não conseguimos conectar. Confira a internet e tente de novo."
          : "Não conseguimos registrar o descadastro agora. Tente de novo em alguns minutos."

// Sem o token, ou com ele recusado, a página não sabe qual e-mail tirar da lista. Mandar tentar de
// novo seria inútil: o caminho é o link de um e-mail mais novo, ou responder o e-mail.
const LinkInvalido = () => (
    <Aviso titulo="Este link expirou ou não é válido" ocultarSuporte>
        <p>
            Não conseguimos identificar qual e-mail descadastrar por este link. Use o link{" "}
            <b>Não quero mais receber estes e-mails</b> do e-mail mais recente que enviamos, ou
            responda esse e-mail pedindo para sair da lista — a gente tira por lá.
        </p>
    </Aviso>
)

const Confirmacao = ({ comRh }: { comRh: boolean }) => (
    <Aviso titulo="Descadastro feito" ocultarSuporte>
        <p>Pronto, este e-mail não vai mais receber nossos avisos.</p>
        {comRh && <p>Obrigado — vamos falar com o RH no endereço informado.</p>}
    </Aviso>
)

type Erros = Partial<Record<keyof Descadastro, string>>

export const DescadastroPage = () => {
    // O token fica na URL e não vai para a sessão (ver lib/session): ele não abre tela nenhuma da
    // empresa, só tira este e-mail da lista.
    const [busca] = useSearchParams()
    const token = busca.get("t")
    const id = useId()

    const [motivo, setMotivo] = useState<string>("")
    const [texto, setTexto] = useState("")
    const [emailDoRh, setEmailDoRh] = useState("")
    const [erros, setErros] = useState<Erros>({})

    const destinatario = useQuery({
        queryKey: ["descadastro", token],
        queryFn: () => fetchDestinatario(token ?? ""),
        enabled: Boolean(token),
        retry: (tentativas, erro) => !linkRecusado(erro) && tentativas < 1,
    })

    const envio = useMutation({
        mutationFn: (dados: Descadastro) => descadastrar(token ?? "", dados),
    })

    if (!token || linkRecusado(destinatario.error) || linkRecusado(envio.error)) {
        return <LinkInvalido />
    }

    if (envio.isSuccess) return <Confirmacao comRh={Boolean(envio.variables.hr_email)} />

    const enviar = (evento: FormEvent<HTMLFormElement>) => {
        evento.preventDefault()
        const resultado = DescadastroSchema.safeParse({
            reason: motivo || undefined,
            text: motivo === "OTHER" ? texto : undefined,
            hr_email: emailDoRh,
        })
        if (!resultado.success) {
            const campos = resultado.error.flatten().fieldErrors
            setErros({
                reason: campos.reason?.[0],
                text: campos.text?.[0],
                hr_email: campos.hr_email?.[0],
            })
            return
        }
        setErros({})
        envio.mutate(resultado.data)
    }

    // O e-mail mascarado só confirma qual endereço sai da lista. Se a busca falhar por outro motivo
    // que não o link, o descadastro continua possível — quem decide é o POST.
    const endereco = destinatario.data?.email_masked

    return (
        <main className="mx-auto flex min-h-dvh max-w-2xl flex-col justify-center px-5 py-16">
            <Marca />
            <div className="rounded-xl border border-line bg-card p-8 shadow-sm">
                <h1 className="font-display text-xl font-black">Parar de receber nossos e-mails</h1>
                <p className="mt-3 text-soft" aria-busy={destinatario.isLoading}>
                    {destinatario.isLoading ? (
                        <Bloco className="h-4 w-2/3 animate-pulse motion-reduce:animate-none" />
                    ) : endereco ? (
                        <>
                            Vamos tirar <b className="text-ink">{endereco}</b> da lista de avisos.
                        </>
                    ) : (
                        "Vamos tirar este e-mail da lista de avisos."
                    )}
                </p>

                <form noValidate onSubmit={enviar} className="mt-6 flex flex-col gap-6">
                    <fieldset aria-describedby={erros.reason ? "erro-motivo" : undefined}>
                        <legend className="mb-3 font-semibold">Por que você quer sair?</legend>
                        <div className="flex flex-col gap-2">
                            {MOTIVOS.map((opcao) => (
                                <label key={opcao.valor} className="flex items-center gap-3">
                                    <input
                                        type="radio"
                                        name="motivo"
                                        value={opcao.valor}
                                        checked={motivo === opcao.valor}
                                        onChange={() => setMotivo(opcao.valor)}
                                        className={`size-4 accent-brand ${FOCO}`}
                                    />
                                    {opcao.rotulo}
                                </label>
                            ))}
                        </div>
                        {erros.reason && (
                            <p id="erro-motivo" className="mt-2 text-sm text-off">
                                {erros.reason}
                            </p>
                        )}
                    </fieldset>

                    {motivo === "OTHER" && (
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor={`${id}-texto`} className="font-semibold">
                                Conte o motivo (opcional)
                            </label>
                            <textarea
                                id={`${id}-texto`}
                                value={texto}
                                onChange={(evento) => setTexto(evento.target.value)}
                                maxLength={LIMITE_DO_TEXTO}
                                rows={3}
                                aria-invalid={Boolean(erros.text)}
                                className={CAMPO}
                            />
                            <span className="text-xs text-faint">
                                {texto.length}/{LIMITE_DO_TEXTO}
                            </span>
                            {erros.text && <span className="text-sm text-off">{erros.text}</span>}
                        </div>
                    )}

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor={`${id}-rh`} className="font-semibold">
                            Qual é o e-mail do RH responsável pela folha?{" "}
                            <span className="font-normal text-faint">(opcional)</span>
                        </label>
                        <input
                            id={`${id}-rh`}
                            type="email"
                            autoComplete="off"
                            value={emailDoRh}
                            onChange={(evento) => setEmailDoRh(evento.target.value)}
                            aria-invalid={Boolean(erros.hr_email)}
                            className={CAMPO}
                        />
                        {erros.hr_email && (
                            <span className="text-sm text-off">{erros.hr_email}</span>
                        )}
                    </div>

                    {envio.isError && (
                        <p
                            role="alert"
                            className="rounded-lg bg-off-wash px-4 py-3 text-sm text-off"
                        >
                            {mensagemDe(envio.error)}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={envio.isPending}
                        aria-busy={envio.isPending}
                        className={BOTAO}
                    >
                        {envio.isPending && <Spinner />}
                        Descadastrar
                    </button>
                </form>
            </div>
        </main>
    )
}
